import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { logger } from '@/lib/logger';

interface SortOrderUpdate {
  id: string;
  sort_order: number;
}

export const useUpdateItemsSortOrder = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const fallbackDirectUpdate = async (updates: SortOrderUpdate[]) => {
    logger.debug('Using fallback direct update for sort order', { updatesCount: updates.length });
    
    const updatePromises = updates.map(update => 
      supabase
        .from('items')
        .update({ sort_order: update.sort_order })
        .eq('id', update.id)
    );
    
    const results = await Promise.all(updatePromises);
    const errors = results.filter(result => result.error);
    
    if (errors.length > 0) {
      throw new Error(`Failed to update ${errors.length} items directly: ${errors[0].error?.message}`);
    }
    
    return true;
  };

  const retryOperation = async (updates: SortOrderUpdate[], attempt = 1) => {
    const maxRetries = 3;
    const backoffDelay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
    
    try {
      setIsRetrying(attempt > 1);
      
      logger.debug('Attempting to update sort order', {
        attempt,
        updatesCount: updates.length,
        firstUpdate: updates[0]
      });
      
      const { data, error } = await supabase.rpc('update_items_sort_order', {
        item_updates: updates as any
      });
      
      if (error) throw error;
      
      setRetryCount(0);
      setIsRetrying(false);
      return data;
    } catch (error) {
      logger.error('Sort order update failed', {
        attempt,
        error: error.message,
        maxRetries
      });
      
      if (attempt < maxRetries) {
        setRetryCount(attempt);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return retryOperation(updates, attempt + 1);
      } else {
        // Final fallback: use direct table updates
        logger.info('RPC failed, using fallback direct update');
        try {
          return await fallbackDirectUpdate(updates);
        } catch (fallbackError) {
          logger.error('Fallback direct update also failed', {
            error: fallbackError.message
          });
          setIsRetrying(false);
          throw fallbackError;
        }
      }
    }
  };
  
  return useMutation({
    mutationFn: async (updates: SortOrderUpdate[]) => {
      return retryOperation(updates);
    },
    onSuccess: () => {
      // Invalidate all items-optimized queries regardless of filter parameters
      queryClient.invalidateQueries({ queryKey: ['items-optimized'] });
      // Also invalidate legacy items queries for compatibility
      queryClient.invalidateQueries({ queryKey: ['items'] });
      
      if (retryCount > 0) {
        toast({
          title: "Thứ tự đã được cập nhật",
          description: `Đã lưu thành công sau ${retryCount + 1} lần thử.`
        });
      } else {
        toast({
          title: "Thứ tự đã được cập nhật",
          description: "Thứ tự items đã được lưu thành công."
        });
      }
    },
    onError: (error) => {
      logger.error('Final sort order update failure', {
        error: error.message,
        retryCount
      });
      
      toast({
        title: "Lỗi cập nhật thứ tự",
        description: `Không thể cập nhật thứ tự items sau ${retryCount + 1} lần thử. Vui lòng thử lại.`,
        variant: "destructive"
      });
    },
    meta: {
      isRetrying,
      retryCount
    }
  });
};